// src/App.js
import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";

import BubbleButterSite from "./BubbleButterSite";
import ProductsPage from "./ProductsPage";
import CartPage from "./CartPage";
import CheckoutPage from "./CheckoutPage";
import OrderSuccess from "./OrderSuccess";
import ViewOrder from "./ViewOrder";
import LoginPage from "./LoginPage";
import AdminPage from "./AdminPage";
import AdminOrderDetails from "./AdminOrderDetails";
import AdminOrdersList from "./AdminOrdersList";
import AdminProductsPage from "./AdminProductsPage";
import TrackOrderPage from "./TrackOrderPage";
import Contact from "./Contact";
import PrivacyPolicy from "./PrivacyPolicy";
import TermsAndConditions from "./TermsAndConditions";
import Footer from "./components/Footer";

import { CartProvider } from "./contexts/CartContext";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import { UserProvider, useUser } from "./contexts/UserContext";

import { auth } from "./firebase";
import LoadFirestoreCart from "./components/LoadFirestoreCart";

// 🔐 Protected Admin Route
function ProtectedAdminRoute({ children }) {
  const { userData, loading } = useUser();

  if (loading) return <p className="text-center mt-10">Loading...</p>;
  if (!userData || userData.role !== "admin") {
    return <Navigate to="/" replace />;
  }
  return children;
}

// 🌙 App Layout with Theme
function AppLayout() {
  const { darkMode } = useTheme();

  return (
    <div className={darkMode ? "dark" : ""}>
      <div className="min-h-screen flex flex-col bg-white dark:bg-gray-900">
        <div className="flex-grow">
          <Routes>
            <Route path="/" element={<BubbleButterSite />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/success/:orderId" element={<OrderSuccess />} />
            <Route path="/order/:orderNumber" element={<ViewOrder />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/track-order" element={<TrackOrderPage />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />

            <Route
              path="/admin"
              element={
                <ProtectedAdminRoute>
                  <AdminPage />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/order/:orderId"
              element={
                <ProtectedAdminRoute>
                  <AdminOrderDetails />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/orders"
              element={
                <ProtectedAdminRoute>
                  <AdminOrdersList />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/products"
              element={
                <ProtectedAdminRoute>
                  <AdminProductsPage />
                </ProtectedAdminRoute>
              }
            />
          </Routes>
        </div>

        <Footer />
      </div>
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);

  useEffect(() => {
    const unsub = auth.onAuthStateChanged((u) => setUser(u));
    return () => unsub();
  }, []);

  return (
    <UserProvider>
      <CartProvider>
        <ThemeProvider>
          <LoadFirestoreCart user={user} />
          <Router>
            <AppLayout />
          </Router>
        </ThemeProvider>
      </CartProvider>
    </UserProvider>
  );
}

export default App;
